import { IconDental, IconEyeglass, IconStethoscope } from '@tabler/icons-react';
import { BenefitType, OnboardingPeriodReportDto } from '@zorro/clients';
import {
  getFullName,
  logger,
  responseErrorToString,
} from '@zorro/common-utils';
import {
  callEndpoint,
  exportEmployeePlanList,
  getApplicantsFromInsured,
  getDefaultWhoIsEnrolled,
  showErrorNotification,
} from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

import { exportSupplementalPlanList } from '../../../../../ManualSubmissionFlow';

type OnClickDownloadPlansProps = {
  onboardingPeriod: OnboardingPeriodReportDto;
};

const onClickDownloadMedicalPlans = async ({
  onboardingPeriod,
}: OnClickDownloadPlansProps) => {
  const { id: onboardingPeriodId, employeeId } = onboardingPeriod;
  try {
    const [insured, employee, benefits] = await Promise.all([
      callEndpoint({
        method: 'insuredControllerFindInsuredForPeriod',
        params: [onboardingPeriodId],
      }),
      callEndpoint({
        method: 'employeesControllerFindOne',
        params: [employeeId],
      }),
      callEndpoint({
        method: 'benefitsControllerGetBenefits',
        params: [onboardingPeriodId],
      }),
    ]);

    const coveredInsured =
      benefits?.majorMedicalBenefit?.insuredPeople ?? insured;
    const insuredIds = getDefaultWhoIsEnrolled(coveredInsured);
    const applicants = getApplicantsFromInsured(
      employee?.email || '',
      coveredInsured,
      onboardingPeriod
    );

    const medicalPlans = await callEndpoint({
      method: 'majorMedicalControllerSearchMedicalPlansForOnboardingPeriod',
      params: [onboardingPeriodId, { applicants, insuredIds }],
    });

    exportEmployeePlanList(medicalPlans, getFullName(employee));
  } catch (error) {
    logger.error(error);
    showErrorNotification({ message: responseErrorToString(error) });
  }
};

const onClickDownloadSupplementalPlans = async ({
  onboardingPeriod,
  method,
  key,
}: OnClickDownloadPlansProps & {
  method: 'dentalControllerSearch' | 'visionControllerSearch';
  key: 'dentalBenefits' | 'visionBenefits';
}) => {
  const { id: onboardingPeriodId, employeeId } = onboardingPeriod;
  try {
    const [employee, benefits] = await Promise.all([
      callEndpoint({
        method: 'employeesControllerFindOne',
        params: [employeeId],
      }),
      callEndpoint({
        method: 'benefitsControllerGetBenefits',
        params: [onboardingPeriodId],
      }),
    ]);

    const insuredIds = Array.from(
      new Set(
        benefits?.[key]?.flatMap(({ insuredPeople }) =>
          insuredPeople ? getDefaultWhoIsEnrolled(insuredPeople) : []
        )
      )
    );

    const supplementalPlans = await callEndpoint({
      method,
      params: [onboardingPeriodId, { insuredIds }],
    });

    exportSupplementalPlanList(supplementalPlans, getFullName(employee));
  } catch (error) {
    logger.error(error);
    showErrorNotification({ message: responseErrorToString(error) });
  }
};

export const getEmployeePagePlansBenefitHeaderConfig = (
  benefitType: BenefitType
) => {
  const dentalConfig = {
    icon: IconDental,
    title: 'Dental plans',
    onDownload: async (props: OnClickDownloadPlansProps) =>
      onClickDownloadSupplementalPlans({
        ...props,
        method: 'dentalControllerSearch',
        key: 'dentalBenefits',
      }),
  };
  return $enum.mapValue(benefitType).with({
    [BenefitType.MAJOR_MEDICAL]: {
      icon: IconStethoscope,
      title: 'Medical plans',
      onDownload: onClickDownloadMedicalPlans,
    },
    [BenefitType.SUPPLEMENTAL_DENTAL]: dentalConfig,
    [BenefitType.SUPPLEMENTAL_COMBINED_DENTAL_VISION]: dentalConfig,
    [BenefitType.SUPPLEMENTAL_VISION]: {
      icon: IconEyeglass,
      title: 'Vision plans',
      onDownload: async (props) =>
        onClickDownloadSupplementalPlans({
          ...props,
          method: 'visionControllerSearch',
          key: 'visionBenefits',
        }),
    },
    [BenefitType.SUPPLEMENTAL_ACCIDENT]: undefined,
    [BenefitType.SUPPLEMENTAL_ACCIDENTAL_DEATH_AND_DISMEMBERMENT]: undefined,
    [BenefitType.SUPPLEMENTAL_CRITICAL_ILLNESS]: undefined,
    [BenefitType.SUPPLEMENTAL_FLEXIBLE_SPENDING_ACCOUNT]: undefined,
    [BenefitType.SUPPLEMENTAL_HOSPITAL]: undefined,
    [BenefitType.SUPPLEMENTAL_LIFE]: undefined,
    [BenefitType.SUPPLEMENTAL_OTHER]: undefined,
    [$enum.handleUnexpected]: undefined,
  });
};
